import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Icon, palette } from '@m12s/component-library';
import { faFileAlt } from '@fortawesome/pro-regular-svg-icons';

import { JobDocsDrawer } from 'components/JobDocsDrawer';

// Figma (Current Job Modal / View Documents footer): a full-width 72px white
// bar with a 1px Grey/400 top border; centered 24px file icon + 20px semibold
// Grey/700 label with a 12px gap.
const FooterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  min-height: 72px;
  flex-shrink: 0;
  background: ${palette.White};
  border: none;
  border-top: 1px solid ${palette.Grey400};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  color: ${palette.Grey700};
  cursor: pointer;

  &:hover {
    background: ${palette.Grey100};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${palette.Blue500};
    outline-offset: -2px;
  }
`;

/**
 * The "View Documents" footer bar — the single entry point to job documents.
 * Product rule: embeds open via approved surfaces — the footer is only a
 * trigger that opens the JobDocsDrawer, never an inline iframe. The drawer
 * (and its embed) mounts only while open, so an untouched footer never boots
 * the iframe.
 */
const JobDocsFooter = ({
  embedZoneId,
  gridUrl,
  gridPath,
  partNumber,
  operationId,
}) => {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      <FooterButton
        type="button"
        data-testid="job-docs-footer"
        disabled={!gridUrl}
        onClick={() => {
          return setDrawerOpen(true);
        }}
      >
        <Icon icon={faFileAlt} size="1.5rem" />
        {t('View Documents')}
      </FooterButton>
      <JobDocsDrawer
        embedZoneId={embedZoneId}
        isOpen={drawerOpen && Boolean(gridUrl)}
        onDismiss={() => {
          return setDrawerOpen(false);
        }}
        gridPath={gridPath}
        partNumber={partNumber}
        operationId={operationId}
      />
    </>
  );
};

JobDocsFooter.propTypes = {
  /** Embed zone id for the drawer-hosted MMEmbeddableZone. */
  embedZoneId: PropTypes.string.isRequired,
  /** Absolute grid URL — gates the trigger button and the drawer open. */
  gridUrl: PropTypes.string,
  /** Grid route (pathname+search) forwarded to the drawer zone's `path`. */
  gridPath: PropTypes.string,
  /**
   * Part number of the operation — threaded to the drawer's contextual header
   * (numeric `0` is a valid id).
   */
  partNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Operation id / sequence number — threaded to the drawer's header. */
  operationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

JobDocsFooter.defaultProps = {
  gridUrl: null,
  gridPath: null,
  partNumber: null,
  operationId: null,
};

export { JobDocsFooter };
